import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { DEFAULT_ORDER_ITEMS_PER_PAGE } from "@/consts/orderFilterConsts";
import { userLoggedOut } from "../auth/authSlice";
import {
  toggleErrorGroupStatusFilter,
  setSelectedErrorGroup,
  setOrderSearchString,
} from "./orderFiltersSlice";

type OrdersState = {
  currentPage: number;
  itemsPerPage: number;
  totalItemsCount: number;
  totalFilteredItemsCount: number;
  orderIdCheckboxes: Record<string, boolean> | undefined;
  invoiceToOrderIds: Record<string, string[]> | undefined;
};

const initialState: OrdersState = {
  currentPage: 0,
  itemsPerPage: DEFAULT_ORDER_ITEMS_PER_PAGE,
  totalItemsCount: 0,
  totalFilteredItemsCount: 0,
  orderIdCheckboxes: undefined,
  invoiceToOrderIds: undefined,
};

const clearCheckboxes = (state: OrdersState) => {
  if (state.orderIdCheckboxes) {
    Object.keys(state.orderIdCheckboxes).forEach((id) => {
      state.orderIdCheckboxes![id] = false;
    });
  }
};

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrderCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setOrderItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
    },
    setOrderTotalItemsCount: (state, action: PayloadAction<number>) => {
      state.totalItemsCount = action.payload;
    },
    setOrderTotalFilteredItemsCount: (state, action: PayloadAction<number>) => {
      state.totalFilteredItemsCount = action.payload;
    },
    initializeOrderIdCheckboxes: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.orderIdCheckboxes = {};
      state.invoiceToOrderIds = action.payload;

      // Flatten all order IDs from all invoices for checkbox initialization
      const allOrderIds = Object.values(action.payload).flat();
      allOrderIds.forEach((id) => {
        state.orderIdCheckboxes![id] = false;
      });
    },
    clearOrderIdCheckboxes: (state) => {
      if (state.orderIdCheckboxes) {
        Object.keys(state.orderIdCheckboxes).forEach((id) => {
          state.orderIdCheckboxes![id] = false;
        });
      }
    },
    toggleOrderIdCheckbox: (state, action: PayloadAction<string>) => {
      if (state.orderIdCheckboxes) {
        state.orderIdCheckboxes[action.payload] = !state.orderIdCheckboxes[action.payload];
      }
    },
    toggleAllOrderIdCheckboxes: (state) => {
      if (state.orderIdCheckboxes) {
        const allCheckboxesChecked = Object.values(state.orderIdCheckboxes).every(checkbox => checkbox);

        Object.keys(state.orderIdCheckboxes).forEach((id) => {
          state.orderIdCheckboxes![id] = !allCheckboxesChecked;
        });
      }
    },
    resetOrderIdCheckboxes: (state) => {
      state.orderIdCheckboxes = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLoggedOut, () => {
        return initialState;
      })
      .addCase(toggleErrorGroupStatusFilter, clearCheckboxes)
      .addCase(setSelectedErrorGroup, clearCheckboxes)
      .addCase(setOrderSearchString, clearCheckboxes);
  },
});

export const {
  setOrderCurrentPage,
  setOrderItemsPerPage,
  setOrderTotalItemsCount,
  setOrderTotalFilteredItemsCount,
  initializeOrderIdCheckboxes,
  clearOrderIdCheckboxes,
  toggleOrderIdCheckbox,
  toggleAllOrderIdCheckboxes,
  resetOrderIdCheckboxes,
} = ordersSlice.actions;

export const selectOrderCurrentPage = (state: RootState) =>
  state.orders.currentPage;
export const selectOrderItemsPerPage = (state: RootState) =>
  state.orders.itemsPerPage;
export const selectOrderTotalItemsCount = (state: RootState) =>
  state.orders.totalItemsCount;
export const selectOrderTotalFilteredItemsCount = (state: RootState) =>
  state.orders.totalFilteredItemsCount;
export const selectOrderIdCheckboxes = (state: RootState) =>
  state.orders.orderIdCheckboxes;
export const selectInvoiceToOrderIds = (state: RootState) =>
  state.orders.invoiceToOrderIds;

export default ordersSlice.reducer;
